/**
 * CONSAI Business App - Admin Dashboard
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Store, Users, FileText, CreditCard, BarChart3, Settings,
  LogOut, Search, Eye, Trash2, RefreshCw, Clock, Crown,
  ChevronLeft, ChevronRight, Loader2, AlertTriangle, X,
  Mail, Phone, MapPin, Building2, Calendar, User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const { admin, adminLogout, checkAdminAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const perPage = 20;

  // Client detail modal
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientActionLoading, setClientActionLoading] = useState(false);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);

  // Logout confirm
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // Fetch clients
  const fetchClients = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (planFilter !== 'all') params.append('plan', planFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('page', page);
      params.append('per_page', perPage);

      const response = await axios.get(`${API_URL}/api/admin/clients?${params}`, {
        withCredentials: true
      });
      setClients(response.data.clients);
      setTotalClients(response.data.total);
    } catch (error) {
      console.error('Error fetching clients:', error);
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    }
  }, [searchQuery, planFilter, statusFilter, page, navigate]);

  // Initial load
  useEffect(() => {
    const initAdmin = async () => {
      setLoading(true);
      // Check if we have admin from location state
      if (location.state?.admin) {
        await Promise.all([fetchStats(), fetchClients()]);
      } else {
        // Check admin auth
        const adminData = await checkAdminAuth();
        if (!adminData) {
          navigate('/admin');
          return;
        }
        await Promise.all([fetchStats(), fetchClients()]);
      }
      setLoading(false);
    };
    initAdmin();
  }, [location.state, checkAdminAuth, navigate, fetchStats, fetchClients]);

  // Refetch when filters change
  useEffect(() => {
    if (!loading) {
      fetchClients();
    }
  }, [searchQuery, planFilter, statusFilter, page, fetchClients, loading]);

  // Handle logout
  const handleLogout = async () => {
    await adminLogout();
    toast.success(language === 'hi' ? 'लॉगआउट सफल' : 'Logged out successfully');
    navigate('/admin');
  };

  // View client details
  const handleViewClient = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/clients/${userId}`, {
        withCredentials: true
      });
      setSelectedClient(response.data);
      setShowClientModal(true);
    } catch (error) {
      toast.error('Failed to fetch client details');
    }
  };

  // Change client plan
  const handleChangePlan = async (userId, newPlan) => {
    setClientActionLoading(true);
    try {
      await axios.put(`${API_URL}/api/admin/clients/${userId}`, {
        current_plan: newPlan
      }, { withCredentials: true });
      toast.success(language === 'hi' ? 'प्लान अपडेट किया गया' : 'Plan updated successfully');
      fetchClients();
      // Update selected client
      if (selectedClient?.user_id === userId) {
        setSelectedClient(prev => ({ ...prev, current_plan: newPlan }));
      }
    } catch (error) {
      toast.error('Failed to update plan');
    } finally {
      setClientActionLoading(false);
    }
  };

  // Toggle client status
  const handleToggleStatus = async (userId, currentStatus) => {
    setClientActionLoading(true);
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      await axios.put(`${API_URL}/api/admin/clients/${userId}`, {
        status: newStatus
      }, { withCredentials: true });
      toast.success(language === 'hi' ? 'स्टेटस अपडेट किया गया' : 'Status updated');
      fetchClients();
      if (selectedClient?.user_id === userId) {
        setSelectedClient(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setClientActionLoading(false);
    }
  };

  // Extend trial
  const handleExtendTrial = async (userId) => {
    setClientActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/clients/${userId}/extend-trial`, {
        days: 7
      }, { withCredentials: true });
      toast.success(language === 'hi' ? 'ट्रायल 7 दिन बढ़ाया गया' : 'Trial extended by 7 days');
      fetchClients();
      handleViewClient(userId);
    } catch (error) {
      toast.error('Failed to extend trial');
    } finally {
      setClientActionLoading(false);
    }
  };

  // Delete client
  const handleDeleteClient = async () => {
    if (!clientToDelete) return;
    setClientActionLoading(true);
    try {
      await axios.delete(`${API_URL}/api/admin/clients/${clientToDelete}`, {
        withCredentials: true
      });
      toast.success(language === 'hi' ? 'क्लाइंट हटाया गया' : 'Client deleted');
      setShowDeleteConfirm(false);
      setShowClientModal(false);
      setClientToDelete(null);
      fetchClients();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete client');
    } finally {
      setClientActionLoading(false);
    }
  };

  // Send password reset
  const handleSendPasswordReset = async (userId) => {
    setClientActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/clients/${userId}/reset-password`, {}, {
        withCredentials: true
      });
      toast.success(language === 'hi' ? 'पासवर्ड रीसेट लिंक भेजा गया' : 'Password reset link sent');
    } catch (error) {
      toast.error('Failed to send reset link');
    } finally {
      setClientActionLoading(false);
    }
  };

  const getPlanBadge = (plan) => {
    switch (plan) {
      case 'premium':
        return <Badge className="bg-amber-100 text-amber-800 border-amber-200"><Crown className="h-3 w-3 mr-1" />Premium</Badge>;
      case 'premium_trial':
        return <Badge className="bg-purple-100 text-purple-800 border-purple-200"><Clock className="h-3 w-3 mr-1" />Trial</Badge>;
      default:
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Starter</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    return status === 'active' 
      ? <Badge className="bg-green-100 text-green-800">Active</Badge>
      : <Badge className="bg-red-100 text-red-800">Inactive</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const totalPages = Math.ceil(totalClients / perPage);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#021945] mx-auto mb-4" />
          <p className="text-slate-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Admin Sidebar */}
      <aside className="hidden lg:flex flex-col fixed left-0 top-0 h-full w-64 bg-[#021945] text-white z-20">
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-center">
            <img src="https://customer-assets.emergentagent.com/job_consai-foundation/artifacts/0mnri4ba_CONSAI%20Logo_compressed.png" alt="CONSAI" className="h-10" />
          </div>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 p-4 space-y-1">
          <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-white/10 text-white" onClick={() => { navigate('/admin/dashboard'); window.scrollTo({top: 0, behavior: 'smooth'}); }} data-testid="admin-nav-dashboard">
            <BarChart3 className="h-5 w-5" />
            <span>{language === 'hi' ? 'डैशबोर्ड' : 'Dashboard'}</span>
          </button>
          <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/70 hover:bg-white/5" onClick={() => navigate('/admin/analytics')} data-testid="admin-nav-analytics">
            <BarChart3 className="h-5 w-5" />
            <span>{language === 'hi' ? 'एनालिटिक्स' : 'Analytics'}</span>
          </button>
          <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/70 hover:bg-white/5" onClick={() => { navigate('/admin/dashboard'); setTimeout(() => document.getElementById('clients-section')?.scrollIntoView({behavior: 'smooth'}), 100); }} data-testid="admin-nav-clients">
            <Users className="h-5 w-5" />
            <span>{language === 'hi' ? 'क्लाइंट्स' : 'Clients'}</span>
          </button>
          <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/70 hover:bg-white/5" onClick={() => navigate('/admin/reports')} data-testid="admin-nav-reports">
            <FileText className="h-5 w-5" />
            <span>{language === 'hi' ? 'रिपोर्ट्स' : 'Reports'}</span>
          </button>
          <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/70 hover:bg-white/5" onClick={() => navigate('/admin/vouchers')} data-testid="admin-nav-payments">
            <CreditCard className="h-5 w-5" />
            <span>{language === 'hi' ? 'वाउचर/भुगतान' : 'Vouchers/Payments'}</span>
          </button>
          <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/70 hover:bg-white/5" onClick={() => navigate('/admin/reports')} data-testid="admin-nav-settings">
            <Settings className="h-5 w-5" />
            <span>{language === 'hi' ? 'सेटिंग्स' : 'Settings'}</span>
          </button>
        </nav>

        {/* Bottom */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <User className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{admin?.name || 'Admin'}</p>
              <p className="text-xs text-white/60 truncate">{admin?.email}</p>
            </div>
          </div>
          <button
            onClick={toggleLanguage}
            className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-white/70 hover:bg-white/5 text-sm"
            data-testid="admin-language-toggle"
          >
            <span className="w-5 text-center">{language === 'en' ? 'अ' : 'A'}</span>
            <span>{language === 'en' ? 'हिंदी' : 'English'}</span>
          </button>
          <button
            onClick={() => setShowLogoutDialog(true)}
            className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-red-400 hover:bg-red-500/10 text-sm"
            data-testid="admin-logout-btn"
          >
            <LogOut className="h-5 w-5" />
            <span>{t('logout')}</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Mobile Header */}
        <header className="lg:hidden bg-[#021945] text-white p-4 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_consai-foundation/artifacts/0mnri4ba_CONSAI%20Logo_compressed.png" alt="CONSAI" className="h-8" />
          </div>
          <button onClick={() => setShowLogoutDialog(true)} className="p-2">
            <LogOut className="h-5 w-5" />
          </button>
        </header>

        {/* Dashboard Content */}
        <main className="p-4 md:p-8 space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-white" data-testid="stat-total-clients">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold font-manrope">{stats?.total_clients || 0}</p>
                    <p className="text-sm text-slate-500">{language === 'hi' ? 'कुल क्लाइंट्स' : 'Total Clients'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white" data-testid="stat-active-users">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <User className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold font-manrope">{stats?.active_users_7d || 0}</p>
                    <p className="text-sm text-slate-500">{language === 'hi' ? 'एक्टिव (7 दिन)' : 'Active (7d)'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white" data-testid="stat-reports">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <FileText className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold font-manrope">{stats?.reports_generated || 0}</p>
                    <p className="text-sm text-slate-500">{language === 'hi' ? 'रिपोर्ट्स' : 'Reports'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white" data-testid="stat-revenue">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <CreditCard className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold font-manrope">₹{stats?.total_revenue || 0}</p>
                    <p className="text-sm text-slate-500">{language === 'hi' ? 'राजस्व' : 'Revenue'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Clients Table */}
          <Card className="bg-white" id="clients-section">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <CardTitle className="font-manrope">
                  {language === 'hi' ? 'क्लाइंट सूची' : 'Client List'}
                </CardTitle>
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder={language === 'hi' ? 'खोजें...' : 'Search...'}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 w-full sm:w-64 h-10"
                      data-testid="client-search-input"
                    />
                  </div>
                  {/* Plan Filter */}
                  <Select value={planFilter} onValueChange={setPlanFilter}>
                    <SelectTrigger className="w-32 h-10" data-testid="plan-filter">
                      <SelectValue placeholder="Plan" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{language === 'hi' ? 'सभी प्लान' : 'All Plans'}</SelectItem>
                      <SelectItem value="starter">Starter</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                      <SelectItem value="premium_trial">Trial</SelectItem>
                    </SelectContent>
                  </Select>
                  {/* Status Filter */}
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-32 h-10" data-testid="status-filter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{language === 'hi' ? 'सभी' : 'All'}</SelectItem>
                      <SelectItem value="active">{language === 'hi' ? 'सक्रिय' : 'Active'}</SelectItem>
                      <SelectItem value="inactive">{language === 'hi' ? 'निष्क्रिय' : 'Inactive'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="">{language === 'hi' ? 'क्लाइंट ID' : 'Client ID'}</TableHead>
                      <TableHead className="">{language === 'hi' ? 'नाम' : 'Name'}</TableHead>
                      <TableHead className="">{language === 'hi' ? 'संपर्क' : 'Contact'}</TableHead>
                      <TableHead className="">{language === 'hi' ? 'व्यवसाय' : 'Business'}</TableHead>
                      <TableHead className="">{language === 'hi' ? 'प्लान' : 'Plan'}</TableHead>
                      <TableHead className="">{language === 'hi' ? 'स्टेटस' : 'Status'}</TableHead>
                      <TableHead className="">{language === 'hi' ? 'एक्शन' : 'Action'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                          {language === 'hi' ? 'कोई क्लाइंट नहीं मिला' : 'No clients found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      clients.map((client) => (
                        <TableRow key={client.user_id}>
                          <TableCell className="font-mono text-sm">{client.unique_client_id}</TableCell>
                          <TableCell className="font-medium">{client.name}</TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {client.email || client.mobile_number || '-'}
                          </TableCell>
                          <TableCell className="text-sm">{client.business_type || '-'}</TableCell>
                          <TableCell>{getPlanBadge(client.current_plan)}</TableCell>
                          <TableCell>{getStatusBadge(client.status)}</TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewClient(client.user_id)}
                              className="h-8"
                              data-testid={`view-client-${client.user_id}`}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              {language === 'hi' ? 'देखें' : 'View'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-500">
                    {language === 'hi' 
                      ? `${totalClients} में से ${(page - 1) * perPage + 1}-${Math.min(page * perPage, totalClients)} दिखा रहे हैं`
                      : `Showing ${(page - 1) * perPage + 1}-${Math.min(page * perPage, totalClients)} of ${totalClients}`}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">{page} / {totalPages}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      data-testid="next-page-btn"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>

      {/* Client Detail Modal */}
      <Dialog open={showClientModal} onOpenChange={setShowClientModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2">
              <User className="h-5 w-5 text-[#021945]" />
              {language === 'hi' ? 'क्लाइंट विवरण' : 'Client Details'}
            </DialogTitle>
            <DialogDescription className="">
              {selectedClient?.unique_client_id}
            </DialogDescription>
          </DialogHeader>

          {selectedClient && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">{language === 'hi' ? 'नाम' : 'Name'}</p>
                  <p className="font-medium">{selectedClient.name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">{language === 'hi' ? 'व्यवसाय का नाम' : 'Business Name'}</p>
                  <p className="font-medium">{selectedClient.business_name || '-'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-slate-400" />
                  <span>{selectedClient.email || '-'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-slate-400" />
                  <span>{selectedClient.mobile_number || '-'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-slate-400" />
                  <span>{selectedClient.business_type || '-'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-slate-400" />
                  <span>{selectedClient.location_city ? `${selectedClient.location_city}, ${selectedClient.location_state}` : '-'}</span>
                </div>
              </div>

              {/* Subscription Info */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium mb-3 font-manrope">{language === 'hi' ? 'सब्सक्रिप्शन' : 'Subscription'}</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">{language === 'hi' ? 'वर्तमान प्लान' : 'Current Plan'}</p>
                    <div className="mt-1">{getPlanBadge(selectedClient.current_plan)}</div>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{language === 'hi' ? 'स्टेटस' : 'Status'}</p>
                    <div className="mt-1">{getStatusBadge(selectedClient.status)}</div>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{language === 'hi' ? 'साइनअप तिथि' : 'Signup Date'}</p>
                    <p className="font-medium">{formatDate(selectedClient.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{language === 'hi' ? 'अंतिम लॉगिन' : 'Last Login'}</p>
                    <p className="font-medium">{formatDate(selectedClient.last_login)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{language === 'hi' ? 'रिपोर्ट खरीदी' : 'Report Purchased'}</p>
                    <p className="font-medium">{selectedClient.report_purchased ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3 pt-4 border-t border-slate-200">
                <h4 className="font-medium font-manrope">{language === 'hi' ? 'एक्शन' : 'Actions'}</h4>
                <div className="flex flex-wrap gap-2">
                  <Select
                    value={selectedClient.current_plan}
                    onValueChange={(value) => handleChangePlan(selectedClient.user_id, value)}
                    disabled={clientActionLoading}
                  >
                    <SelectTrigger className="w-40" data-testid="change-plan-select">
                      <SelectValue placeholder="Change Plan" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="starter">Starter</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                      <SelectItem value="premium_trial">Premium Trial</SelectItem>
                    </SelectContent>
                  </Select>

                  {selectedClient.current_plan === 'premium_trial' && (
                    <Button
                      variant="outline"
                      onClick={() => handleExtendTrial(selectedClient.user_id)}
                      disabled={clientActionLoading}
                      data-testid="extend-trial-btn"
                    >
                      <Clock className="h-4 w-4 mr-2" />
                      {language === 'hi' ? '+7 दिन' : '+7 Days'}
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    onClick={() => handleToggleStatus(selectedClient.user_id, selectedClient.status)}
                    disabled={clientActionLoading}
                    data-testid="toggle-status-btn"
                  >
                    {selectedClient.status === 'active' 
                      ? (language === 'hi' ? 'निष्क्रिय करें' : 'Deactivate')
                      : (language === 'hi' ? 'सक्रिय करें' : 'Activate')}
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => handleSendPasswordReset(selectedClient.user_id)}
                    disabled={clientActionLoading}
                    data-testid="reset-password-btn"
                  >
                    {language === 'hi' ? 'पासवर्ड रीसेट' : 'Reset Password'}
                  </Button>

                  <Button
                    variant="destructive"
                    onClick={() => {
                      setClientToDelete(selectedClient.user_id);
                      setShowDeleteConfirm(true);
                    }}
                    disabled={clientActionLoading}
                    data-testid="delete-client-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {language === 'hi' ? 'हटाएं' : 'Delete'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600 font-manrope">
              <AlertTriangle className="h-5 w-5" />
              {language === 'hi' ? 'क्लाइंट हटाएं' : 'Delete Client'}
            </DialogTitle>
            <DialogDescription className="">
              {t('deleteConfirm')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} data-testid="cancel-delete-btn">
              {t('cancel')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteClient}
              disabled={clientActionLoading}
              data-testid="confirm-delete-btn"
            >
              {clientActionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logout Confirmation Dialog */}
      <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-manrope">{t('logout')}</DialogTitle>
            <DialogDescription className="">
              {t('logoutConfirm')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowLogoutDialog(false)} className="">
              {t('cancel')}
            </Button>
            <Button onClick={handleLogout} className="bg-red-600 hover:bg-red-700 text-white">
              {t('logout')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
